import React, { Fragment, useEffect, useRef, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { useField } from "formik";
import { useAuth } from "hooks/Auth";
import { apiGet } from "helpers/Request";
import { transform as entryTransformer } from "schemas/Entry";
import { transform as channelTransformer } from "schemas/Channel";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import { packClasses } from "Utils";
import SelectBox from "components/FormElements/SelectBox";

export default function SelectentryMetaProperty(props) {
    const formikField = useField(props);
    const field = formikField[0];
    const helpers = formikField[2];

    const [selected, setSelected] = useState({ uuid: null, label: "None" });
    const [entries, setEntries] = useState([]);
    const fieldRef = useRef(null);
    const { channel } = props.metaProps;

    const auth = useAuth();

    const onChange = (val) => {
        const entry = entries.find((e) => e.uuid === val.uuid);
        setSelected({ uuid: entry.uuid, label: entry.title });
        helpers.setValue(entry.uuid);
    };

    useEffect(() => {
        apiGet("/v4/entry", auth.publication, {
            channel: channel,
            show_hidden: true,
        }).then((res) => {
            const entries = res.data.map(entryTransformer);
            const entry = entries.find((entry) => entry.uuid === field.value);
            setEntries(entries);
            if (entry) {
                setSelected({ uuid: entry.uuid, label: entry.title });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, auth.publication]);

    useEffect(() => {
        if (fieldRef.current !== null) {
            return;
        }

        const entry = entries.find((e) => e.uuid === field.value);
        if (!entry) {
            return;
        }
        setSelected({ uuid: entry.uuid, label: entry.title });
        helpers.setValue(entry.uuid);
        fieldRef.current = true;

        return () => {
            fieldRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries]);

    return (
        <Listbox value={selected} onChange={onChange}>
            {({ open }) => (
                <Fragment>
                    <Listbox.Label className="block text-sm font-medium text-gray-700">
                        {field.label}
                    </Listbox.Label>
                    <div className="relative">
                        <Listbox.Button className="focus:outline-none relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 disabled:opacity-50 sm:text-sm">
                            <span className="block truncate">
                                {selected.label}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <SelectorIcon
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                />
                            </span>
                        </Listbox.Button>
                        <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options
                                static
                                className="focus:outline-none absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm"
                            >
                                {entries.map((entry, index) => (
                                    <Listbox.Option
                                        key={entry.uuid}
                                        value={{
                                            uuid: entry.uuid,
                                            label: entry.title,
                                        }}
                                        className={({ active }) =>
                                            packClasses(
                                                active
                                                    ? "bg-green-600 text-white"
                                                    : "text-gray-900",
                                                "relative cursor-default select-none py-2 pl-8 pr-4"
                                            )
                                        }
                                    >
                                        {({ active }) => (
                                            <Fragment>
                                                <span
                                                    className={packClasses(
                                                        entry.uuid ===
                                                            field.value
                                                            ? "font-semibold"
                                                            : "font-normal",
                                                        "block truncate"
                                                    )}
                                                >
                                                    {entry.title}
                                                </span>
                                                {entry.uuid === field.value && (
                                                    <span
                                                        className={packClasses(
                                                            active
                                                                ? "text-white"
                                                                : "text-green-600",
                                                            "absolute inset-y-0 left-0 flex items-center pl-1.5"
                                                        )}
                                                    >
                                                        <CheckIcon
                                                            className="h-5 w-5"
                                                            aria-hidden="true"
                                                        />
                                                    </span>
                                                )}
                                            </Fragment>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                </Fragment>
            )}
        </Listbox>
    );
}

export const Property = SelectentryMetaProperty;

export const Label = "Select Entry";

export function Configure(props) {
    const [channels, setChannels] = useState([]);

    const auth = useAuth();
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current) {
            return;
        }

        apiGet("/v4/channel", auth.publication).then((res) => {
            setChannels(channelTransformer(res));
            ref.current = true;
        });

        return () => {
            setChannels([]);
            ref.current = null;
        };
    }, [auth.publication]);

    return (
        <div>
            <SelectBox
                name={`${props.fieldName}.channel`}
                options={channels.map((ch) => ({
                    label: ch.title,
                    value: ch.id,
                }))}
            />
        </div>
    );
}
